import { Box, Button, ButtonBase, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core'
import {
  ListPurchases,
  ListPurchasesVariables,
  ListPurchases_listPurchases_purchases
} from './__generated__/ListPurchases'
import { PurchaseSortBy, PurchaseSortOrder } from './types'
import React, { useState } from 'react'

import { AdvancedSearch } from '../../ui/AdvancedSearch'
import { ApolloConsumer } from '@apollo/react-common'
import { Check, GetApp } from '@material-ui/icons'
import { FormattedDateTime } from '../formatted-date-time'
import { LoadMask, useToasts } from '@vestaboard/installables'

import { SuperGrid } from '../../ui/SuperGrid'
import { TruncatedId } from '../truncated-id'
import { csvDownload, csvFormat } from '../../utils'
import { gql } from 'apollo-boost'
import moment from 'moment'
import { useInvalidateActivationCode } from '../plus/hooks/useInvalidateActivationCode'
import { useQuery } from '@apollo/react-hooks'
import { useUnInvalidateActivationCode } from '../plus/hooks/useUnInvalidateActivationCode'

const PLUS_PRODUCT_QUERY = gql`
  query ListPurchases(
    $terms: String
    $page: Int
    $perPage: Int
    $sortBy: PurchaseSortBy
    $sortOrder: PurchaseSortOrder
    $activationCodeState: String
  ) {
    listPurchases(
      terms: $terms
      perPage: $perPage
      page: $page
      sortBy: $sortBy
      sortOrder: $sortOrder
      activationCodeState: $activationCodeState
    ) {
      purchases {
        id
        optOutOfRenewal
        account {
          id
          person {
            id
          }
        }
        emailAddress
        created
        purchaseSession {
          id
          shopifyOrderId
          shopifyOrderName
        }
        lineItems {
          id
          activationCode {
            id
            code
            isInvalidated
          }
          productApplication {
            id
            nextBillingDate
            board {
              id
            }
          }
        }
      }
      pagination {
        page
        total
        perPage
      }
    }
  }
`

interface FlatPurchase {
  purchaseId: string
  personId?: string
  email?: string
  purchaseDate: number
  shopifyOrderId?: string
  shopifyOrderName?: string
  productApplicationIds?: string[]
  optOutOfRenewal: boolean
  boardIds?: string[]
  nextBillingDates?: number[]
  activationCodes: {
    id: string
    code: string
    isInvalidated: boolean
  }[]
}

export const OrdersListPage = () => {
  const perPage = 50
  const [sort, setSort] = useState<{
    by: PurchaseSortBy
    order: PurchaseSortOrder
  }>({
    by: PurchaseSortBy.created,
    order: PurchaseSortOrder.desc
  })
  const [activationCodeState, setActivationCodeState] = useState('all')
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportDateMin, setExportDateMin] = useState('')
  const [exportDateMax, setExportDateMax] = useState('')
  const [exporting, setExporting] = useState(false)
  const { addToast } = useToasts()
  const variables = {
    terms: (searchValue || '').trim(),
    sortBy: sort.by,
    sortOrder: sort.order,
    activationCodeState: activationCodeState === 'all' ? null : activationCodeState
  }
  const { data, loading, error } = useQuery<ListPurchases, ListPurchasesVariables>(PLUS_PRODUCT_QUERY, {
    variables: {
      ...variables,
      perPage,
      page
    }
  })
  const [invalidateActivationCode] = useInvalidateActivationCode()
  const [unInvalidateActivationCode] = useUnInvalidateActivationCode()

  const plusPurchases = data?.listPurchases.purchases || []
  const pagination = data?.listPurchases?.pagination

  const columns = [
    {
      title: 'Order ID',
      key: PurchaseSortBy.id,
      render: (plusApplication: FlatPurchase) =>
        plusApplication.purchaseId ? (
          <TruncatedId value={plusApplication.purchaseId} path={`/orders/${plusApplication.purchaseId}`} />
        ) : null,
      width: 150
    },
    {
      title: 'Shopify Order ID',
      key: PurchaseSortBy.shopifyOrderId,
      render: (plusApplication: FlatPurchase) => (
        <a
          style={{
            color: '#3f51b5',
            fontSize: '1rem',
            textDecoration: 'none'
          }}
          target='_blank'
          rel='noopener noreferrer'
          href={`https://vestaboard-plus.myshopify.com/admin/orders/${plusApplication?.shopifyOrderId}`}>
          {plusApplication?.shopifyOrderId || ''}
        </a>
      ),
      width: 150
    },
    {
      title: 'Shopify Order Name',
      key: PurchaseSortBy.shopifyOrderName,
      render: (plusApplication: FlatPurchase) => plusApplication?.shopifyOrderName,
      width: 150
    },
    {
      title: 'Purchase Date',
      key: PurchaseSortBy.created,
      render: (plusApplication: FlatPurchase) => (
        <FormattedDateTime format='MM/DD/YYYY' value={plusApplication.purchaseDate} />
      ),
      width: 120
    },
    {
      title: 'Billing Date(s)',
      render: (plusApplication: FlatPurchase) => (
        <>
          {plusApplication.nextBillingDates &&
            plusApplication.nextBillingDates.map(date => (
              <Box>
                <FormattedDateTime format='MM/DD/YYYY' value={date} />
              </Box>
            ))}
        </>
      ),
      width: 120
    },
    {
      title: 'Person ID',
      key: PurchaseSortBy.personId,
      render: (plusApplication: FlatPurchase) =>
        plusApplication?.personId ? (
          <TruncatedId value={plusApplication?.personId || ''} path={`/person/${plusApplication?.personId}`} />
        ) : null,
      width: 150
    },
    {
      title: 'Person Email',
      key: PurchaseSortBy.email,
      render: (plusApplication: FlatPurchase) => plusApplication.email || '',
      width: 200
    },
    {
      title: 'Is Activated?',
      key: PurchaseSortBy.productApplicationId,
      render: (plusApplication: FlatPurchase) => (plusApplication.productApplicationIds?.length ? <Check /> : null),
      width: 100
    },
    {
      title: 'Subscription ID(s)',
      key: PurchaseSortBy.productApplicationId,
      render: (plusApplication: FlatPurchase) =>
        plusApplication.productApplicationIds ? (
          <>
            {plusApplication.productApplicationIds.map(id => (
              <TruncatedId value={id} path={`/plus/${id}`} />
            ))}
          </>
        ) : null,
      width: 150
    },
    {
      title: 'Board ID(s)',
      render: (plusApplication: FlatPurchase) =>
        plusApplication.boardIds ? (
          <>
            {plusApplication.boardIds.map(id => (
              <TruncatedId value={id} path={`/boards/${id}`} />
            ))}
          </>
        ) : null,
      width: 150
    },
    {
      title: 'Opted Out of Renewal',
      render: (plusApplication: FlatPurchase) => (plusApplication.optOutOfRenewal ? <Check /> : null),
      width: 150
    },
    {
      title: 'Has Valid Codes',
      render: (plusApplication: FlatPurchase) =>
        plusApplication.activationCodes.filter(code => !code?.isInvalidated).length ? <Check /> : null,
      width: 150
    },
    {
      title: 'Has Invalid Codes',
      render: (plusApplication: FlatPurchase) =>
        plusApplication.activationCodes.filter(code => !!code?.isInvalidated).length ? <Check /> : null,
      width: 150
    },
    {
      title: 'Activation Codes',
      render: (plusApplication: FlatPurchase) => (
        <>
          {(plusApplication?.activationCodes || []).map(activationCode => (
            <Chip
              style={{
                marginBottom: 4
              }}
              color={activationCode?.isInvalidated ? 'secondary' : 'primary'}
              label={
                <>
                  {activationCode?.code}
                  {' | '}
                  <ButtonBase
                    onClick={async () => {
                      if (activationCode?.isInvalidated) {
                        await unInvalidateActivationCode({
                          variables: {
                            id: activationCode?.id || ''
                          }
                        })
                      } else {
                        await invalidateActivationCode({
                          variables: {
                            id: activationCode?.id || ''
                          }
                        })
                      }
                    }}>
                    {activationCode?.isInvalidated ? 'Uninvalidate' : 'Invalidate'}
                  </ButtonBase>
                </>
              }
              key={activationCode?.id}
            />
          ))}
        </>
      ),
      width: 250
    }
  ]

  const createFlatPurchase = (app: ListPurchases_listPurchases_purchases) =>
    ({
      purchaseId: app.id,
      personId: app?.account?.person?.id,
      email: app?.emailAddress,
      purchaseDate: app.created,
      shopifyOrderId: app.purchaseSession?.shopifyOrderId,
      shopifyOrderName: app.purchaseSession?.shopifyOrderName,
      productApplicationIds: app?.lineItems?.map(lineItem => lineItem?.productApplication?.id)?.filter(id => !!id),
      optOutOfRenewal: app.optOutOfRenewal,
      boardIds: app?.lineItems?.map(lineItem => lineItem?.productApplication?.board?.id),
      nextBillingDates: app?.lineItems?.map(lineItem => lineItem?.productApplication?.nextBillingDate),
      activationCodes: app?.lineItems?.map(listItem => listItem.activationCode) || []
    } as FlatPurchase)

  const visiblePlusApplications = (plusPurchases || []).map(createFlatPurchase)

  return (
    <SuperGrid
      title='Plus Orders'
      setSort={setSort}
      columns={columns}
      rows={visiblePlusApplications}
      sortBy={sort.by}
      sortOrder={sort.order}
      pagination={pagination}
      setPage={setPage}
      error={!!error}
      loading={loading}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      renderSearch={
        <AdvancedSearch
          searchTypes={[
            {
              key: 'all',
              title: 'All',
              type: 'text' as 'text'
            },
            {
              key: 'Valid',
              title: 'Valid Unused',
              type: 'text' as 'text'
            },
            {
              key: 'Invalid',
              title: 'Invalid Unused',
              type: 'text' as 'text'
            },
            {
              key: 'Activated',
              title: 'Activated',
              type: 'text' as 'text'
            }
          ]}
          value={searchValue}
          setValue={value => {
            setPage(0)
            setSearchValue((value as string) || '')
          }}
          searchType={activationCodeState}
          setSearchType={setActivationCodeState}
        />
      }
      centerButtons={
        <Box display='flex'>
          <Button variant='outlined' onClick={() => setExportDialogOpen(true)}>
            Export CSV
          </Button>
          <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
            <DialogTitle>Export Plus Orders</DialogTitle>
            <DialogContent>
              <Box display='flex' flexDirection='column' style={{ gap: 16, minWidth: 300 }}>
                <TextField
                  variant='standard'
                  type='date'
                  label='Purchase Date From'
                  value={exportDateMin}
                  onChange={e => setExportDateMin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  variant='standard'
                  type='date'
                  label='Purchase Date To'
                  value={exportDateMax}
                  onChange={e => setExportDateMax(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  onClick={() => {
                    setExportDateMin('')
                    setExportDateMax('')
                  }}>
                  CLEAR DATES (Full Report)
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
              <ApolloConsumer>
                {apollo => (
                  <>
                    {exporting ? <LoadMask /> : null}
                    <Button
                      endIcon={<GetApp />}
                      variant='outlined'
                      disabled={exporting}
                      onClick={async () => {
                        if (exporting) return
                        setExporting(true)
                        const dateMin = exportDateMin ? moment(exportDateMin).startOf('day').valueOf() : 0
                        const dateMax = exportDateMax ? moment(exportDateMax).endOf('day').valueOf() : Infinity
                        const hasDateFilter = !!(exportDateMin || exportDateMax)
                        const exportPerPage = 100
                        const results: any[] = []

                        try {
                          let currentPage = 0
                          let done = false

                          while (!done) {
                            const result = await apollo.query({
                              fetchPolicy: 'network-only',
                              query: PLUS_PRODUCT_QUERY,
                              variables: {
                                ...variables,
                                sortBy: PurchaseSortBy.created,
                                sortOrder: PurchaseSortOrder.desc,
                                perPage: exportPerPage,
                                page: currentPage
                              }
                            })

                            const purchases = result.data.listPurchases.purchases
                            const pag = result.data.listPurchases.pagination

                            for (const purchase of purchases) {
                              const flat = createFlatPurchase(purchase)
                              const created = flat.purchaseDate || 0

                              if (hasDateFilter && created > dateMax) continue
                              if (hasDateFilter && created < dateMin) {
                                done = true
                                break
                              }

                              const { activationCodes, ...rest } = flat

                              const invalidatedActivationCodes = activationCodes
                                .filter(code => code?.isInvalidated)
                                .map(code => code?.code)
                                .join(', ')

                              const validActivationCodes = activationCodes
                                .filter(code => !code?.isInvalidated)
                                .map(code => code?.code)
                                .join(', ')

                              results.push({
                                ...rest,
                                boardIds: flat.boardIds?.filter(id => !!id).join(', '),
                                productApplicationIds: flat.productApplicationIds?.filter(id => !!id).join(', '),
                                activationCodes: activationCodes
                                  .map(code => code?.code)
                                  .filter(id => !!id)
                                  .join(', '),
                                isActivated: !!flat.productApplicationIds?.length,
                                purchaseDate: flat.purchaseDate
                                  ? moment(flat.purchaseDate).format('YYYY-MM-DD hh:mm a')
                                  : null,
                                nextBillingDates: flat.nextBillingDates
                                  ? flat.nextBillingDates
                                      .filter(date => !!date)
                                      .map(date => moment(date).format('YYYY-MM-DD hh:mm a'))
                                      .join(', ')
                                  : null,
                                invalidatedActivationCodes,
                                validActivationCodes,
                                hasValidActivationCodes: !!validActivationCodes?.length,
                                hasInvalidatedActivationCodes: !!invalidatedActivationCodes?.length
                              })
                            }

                            if ((currentPage + 1) * exportPerPage >= pag.total) {
                              done = true
                            }

                            currentPage++
                          }

                          if (results.length > 0) {
                            const csv = csvFormat(results, Object.keys(results[0]))
                            csvDownload(csv)
                          } else {
                            addToast('No records found in the selected date range', { appearance: 'error' })
                          }
                        } catch (e) {
                          console.error(e)
                          addToast('Error exporting CSV', { appearance: 'error' })
                        }

                        setExporting(false)
                      }}>
                      Export CSV
                    </Button>
                  </>
                )}
              </ApolloConsumer>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  )
}
